<template>
  <div>
    <a-form-model ref="ruleForm" :model="form" :rules="rules" class="fulfillment-revision">
      <h1 class="page-title mb-3">
        {{ $t('fulfillment.reviseDeliveryOrder') }}
      </h1>
      <a-card class="mb-3">
        <template slot="title">
          {{ $t('fulfillment.detailDeliveryOrder') }}
        </template>
        <a-spin :spinning="isLoading">
          <a-row :gutter="16">
            <a-col :span="12">
              <a-form-model-item ref="orderDate" :label="$t('fulfillment.order_date')" prop="orderDate">
                <a-input v-model="form.orderDate" :placeholder="$t('fulfillment.order_date')" disabled />
              </a-form-model-item>
            </a-col>
            <a-col :span="12">
              <a-form-model-item ref="orderNumber" :label="$t('fulfillment.order_id')" prop="orderNumber">
                <a-input v-model="form.orderNumber" :placeholder="$t('fulfillment.order_id')" disabled />
              </a-form-model-item>
            </a-col>
            <a-col :span="12">
              <a-form-model-item ref="fulfillmentNumber" :label="$t('fulfillment.shippingId')" prop="fulfillmentNumber">
                <a-input v-model="form.fulfillmentNumber" :placeholder="$t('fulfillment.shippingId')" disabled />
              </a-form-model-item>
            </a-col>
            <a-col :span="12">
              <a-form-model-item ref="business" :label="$t('fulfillment.business')" prop="business">
                <a-input v-model="form.business" :placeholder="$t('fulfillment.business')" disabled />
              </a-form-model-item>
            </a-col>
            <a-col :span="12">
              <a-form-model-item ref="storeName" :label="$t('fulfillment.store')" prop="storeName">
                <a-input v-model="form.storeName" :placeholder="$t('fulfillment.store')" disabled />
              </a-form-model-item>
            </a-col>
            <a-col :span="12">
              <a-form-model-item ref="warehouse" :label="$t('fulfillment.warehouse')" prop="warehouse">
                <a-select
                  v-model="form.warehouse"
                  class="w-100"
                  show-search
                  :placeholder="$t('utils.choose_a_warehouse')"
                  :options="warehouseList"
                  option-filter-prop="children"
                  :filter-option="filterOption"
                  :disabled="isLoading"
                  @change="handleChangeWarehouse"
                />
              </a-form-model-item>
            </a-col>
            <a-col :span="12">
              <a-form-model-item ref="bookingStatus" :label="$t('fulfillment.booking_status')" prop="bookingStatus" class="mb-0">
                <a-input v-model="form.bookingStatus" :placeholder="$t('fulfillment.booking_status')" disabled />
              </a-form-model-item>
            </a-col>
            <a-col :span="12">
              <a-form-model-item ref="status" :label="$t('fulfillment.status')" prop="status" class="mb-0">
                <a-input v-model="form.status" :placeholder="$t('fulfillment.status')" disabled />
              </a-form-model-item>
            </a-col>
          </a-row>
        </a-spin>
      </a-card>
      <a-card :title="$t('fulfillment.detailItemProduct')">
        <template slot="extra">
          <a-button type="primary" @click="handleAddItem">
            {{ $t('fulfillment.addItem') }}
          </a-button>
        </template>
        <a-spin :spinning="isLoading">
          <table class="w-100">
            <thead>
              <tr>
                <th style="width: 33%">
                  {{ $t('fulfillment.product') }}
                </th>
                <th style="width: 20%">
                  {{ $t('common.sku') }}
                </th>
                <th style="width: 12%">
                  {{ $t('common.quantity') }}
                </th>
                <th style="width: 8%">
                  {{ $t('fulfillment.stock') }}
                </th>
                <th style="width: 7%">
                  {{ $t('common.uom') }}
                </th>
                <th style="width: 13%">
                  {{ $t('fulfillment.referenceItem') }}
                </th>
                <th class="text-center" style="width: 7%">
                  #
                </th>
              </tr>
            </thead>
            <tbody v-if="!isLoading && !form.items.length">
              <tr>
                <td colspan="5" class="text-center">
                  {{ $t('fulfillment.noData') }}
                </td>
              </tr>
            </tbody>
            <tbody v-for="(item, index) in form.items" v-else :key="index">
              <tr :class="{'no-border-row': item.sku && (!item.is_on_warehouse || item.availability === 0 || item.availability === '0')}">
                <td>
                  {{ item.title }}
                </td>
                <td>
                  <a-form-model-item
                    class="mb-0"
                    :prop="'items.' + index + '.sku'"
                    :rules="{
                      required: true,
                      message: $t('fulfillment.skuRequired'),
                      trigger: 'change',
                    }"
                  >
                    <a-select
                      v-model="item.sku"
                      :placeholder="$t('fulfillment.searchSku')"
                      show-search
                      :label-in-value="false"
                      :not-found-content="fetching ? undefined : null"
                      :filter-option="false"
                      style="width: 100%"
                      class="sku-revision"
                      @search="handleSearch"
                      @change="handleChange($event, index)"
                      @blur="handleResetSearch"
                    >
                      <a-spin v-if="fetching" slot="notFoundContent" size="small" />
                      <a-select-opt-group v-for="inv in inventories" :key="inv.id">
                        <span slot="label"> {{ inv.sku }}</span>
                        <a-select-option
                          :value="`${inv.product_id}.${inv.id}.${inv.sku}.${inv.title}.${inv.unit}.${inv.availability}`"
                          :disabled="selectedSKU.includes(inv.sku)"
                        >
                          {{ inv.title }}
                        </a-select-option>
                      </a-select-opt-group>
                    </a-select>
                  </a-form-model-item>
                </td>
                <td>
                  <a-form-model-item
                    class="mb-0"
                    :prop="'items.' + index + '.quantity'"
                    :rules="{ validator: validateQty, trigger: 'blur' }"
                  >
                    <InputNumber
                      v-model="item.quantity"
                      style="width: 100%"
                      :disabled="isLoading"
                    />
                  </a-form-model-item>
                </td>
                <td>
                  {{ item.availability }}
                </td>
                <td>
                  {{ item.uom }}
                </td>
                <td>
                  <a-tooltip v-if="getReferencedItem(item.order_item_reference_id)" placement="top">
                    <template slot="title">
                      {{ getReferencedItem(item.order_item_reference_id).catalog_title }}
                    </template>
                    <div class="ref-badge">
                      <a-icon type="link" style="font-size: 11px; margin-right: 4px" />
                      <span class="ref-badge__sku">{{ getReferencedItem(item.order_item_reference_id).sku }}</span>
                    </div>
                  </a-tooltip>
                  <span v-else style="color: #ccc; font-size: 12px">—</span>
                </td>
                <td class="text-center">
                  <a-tooltip v-if="item.sku && itemsWithSku.length <= 1" :title="$t('fulfillment.cannotDeleteLastItem')">
                    <a-icon type="delete" style="color: #ccc; cursor: not-allowed" />
                  </a-tooltip>
                  <template v-else>
                    <a-popconfirm
                      v-if="item.sku"
                      :title="$t('fulfillment.confirmDeleteItem', { sku: item.sku })"
                      :ok-text="$t('common.ok')"
                      :cancel-text="$t('common.cancel')"
                      @confirm="handleDeleteItem(index)"
                    >
                      <a-icon type="delete" style="color: #e00000; cursor: pointer" />
                    </a-popconfirm>
                    <a-icon v-else type="delete" style="color: #e00000; cursor: pointer" @click="handleDeleteItem(index)" />
                  </template>
                </td>
              </tr>
              <tr v-if="item.sku && (!item.is_on_warehouse || item.availability === 0 || item.availability === '0')">
                <td style="padding-top: 0; padding-bottom: 16px; border-top: none; vertical-align: top;">
                  <div v-if="!item.is_on_warehouse" style="font-size: 12px; color: red; line-height: 1.2; margin-top: -8px;">
                    {{ $t('fulfillment.skuNotFoundWarehouse') }}
                  </div>
                </td>
                <td colspan="2" style="padding-top: 0; padding-bottom: 16px; border-top: none;" />
                <td style="padding-top: 0; padding-bottom: 16px; border-top: none; vertical-align: top;">
                  <div v-if="item.availability === 0 || item.availability === '0'" style="font-size: 11px; color: #ff4d4f; line-height: 1.2; margin-top: -8px;">
                    {{ $t('fulfillment.hasZeroStockItem') }}
                  </div>
                </td>
                <td colspan="3" style="padding-top: 0; padding-bottom: 16px; border-top: none;" />
              </tr>
            </tbody>
          </table>
        </a-spin>
      </a-card>
      <div class="footer">
        <a-button
          type="primary"
          size="large"
          class="mr-4"
          ghost
          :disabled="isLoading"
          @click="goBack"
        >
          {{ $t('utils.cancel') }}
        </a-button>
        <a-button
          type="primary"
          size="large"
          :loading="isLoading"
          @click="submitRevision"
        >
          {{ $t('utils.save') }}
        </a-button>
      </div>
    </a-form-model>

    <!-- Modal: Pilih Referensi Item -->
    <a-modal
      v-model="addItemModalVisible"
      :title="$t('fulfillment.selectReferenceItem')"
      :footer="null"
      width="600px"
    >
      <p style="color: #666; margin-bottom: 16px">
        {{ $t('fulfillment.selectReferenceItemDesc') }}
      </p>
      <div style="max-height: 360px; overflow-y: auto; padding-right: 4px">
        <div
          v-for="lineItem in distinctOriginalLineItems"
          :key="lineItem.order_line_item_id"
          class="reference-item-row"
          :class="{ 'reference-item-row--selected': selectedReferenceId === lineItem.order_line_item_id }"
          @click="selectedReferenceId = lineItem.order_line_item_id"
        >
          <div class="reference-item-row__radio">
            <a-radio :checked="selectedReferenceId === lineItem.order_line_item_id" />
          </div>
          <div class="reference-item-row__info">
            <div class="reference-item-row__title">
              {{ lineItem.catalog_title }}
            </div>
            <div class="reference-item-row__sku">
              SKU: {{ lineItem.sku }} &nbsp;|&nbsp; Qty: {{ lineItem.quantity }} &nbsp;|&nbsp; {{ lineItem.uom }}
            </div>
          </div>
        </div>
      </div>
      <div style="text-align: right; margin-top: 20px">
        <a-button style="margin-right: 8px" @click="addItemModalVisible = false">
          {{ $t('common.cancel') }}
        </a-button>
        <a-button type="primary" :disabled="!selectedReferenceId" @click="handleConfirmAddItem">
          {{ $t('common.ok') }}
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { getFulfillmentDetail, submitFulfillmentRevision } from '@/api/fulfillment'
import { getCatalogListGR } from '@/api/inventory'
import { setWordingFulfillmentState } from '@/utils/fulfillment/index'
import InputNumber from '@/components/Input/InputNumber.vue'

export default {
  name: 'FulfillmentRevision',
  components: { InputNumber },
  data() {
    return {
      isLoading: false,
      fetching: false,
      detail: null,
      form: {
        orderDate: '',
        orderNumber: '',
        fulfillmentNumber: '',
        business: '',
        storeName: '',
        warehouse: '',
        bookingStatus: '',
        status: '',
        items: [],
      },
      rules: {
        warehouse: [{ required: true, message: this.$t('fulfillment.fieldRequired', [this.$t('fulfillment.warehouse')]), trigger: 'change' }],
      },
      bookingState: {
        BOOKED: {
          title: 'Booked',
          class: 'allocated-state',
        },
        ALLOCATED: {
          title: 'Booked',
          class: 'allocated-state',
        },
        SUCCESS_ALLOCATED: {
          title: 'Booked',
          class: 'allocated-state',
        },
        ALLOCATION_RELEASED: {
          title: 'Booked',
          class: 'allocated-state',
        },
        NOT_YET_ALLOCATED: {
          title: 'Pending',
          class: 'unallocated-state',
        },
        ALLOCATION_FAILED: {
          title: 'OOS',
          class: 'oos-state',
        },
        OUT_OF_STOCK: {
          title: 'OOS',
          class: 'oos-state',
        },
        OOS: {
          title: 'OOS',
          class: 'oos-state',
        },
      },
      warehouseList: [],
      inventories: [],
      originalLineItems: [],
      addItemModalVisible: false,
      selectedReferenceId: null,
    }
  },
  computed: {
    businessId() {
      return this.$store.state.user.restriction_base === 'Warehouse'
        ? Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
        : String(this.$route.query['business_id'] || this.$route.query['business_ids'] || Object.keys(this.$store.getters['user/businessListObjectByKey']))
    },
    selectedSKU() {
      return this.form.items.map((obj2) => obj2.sku)
    },
    itemsWithSku() {
      return this.form.items.filter((item) => item.sku)
    },
    distinctOriginalLineItems() {
      const uniqueMap = new Map()

      this.originalLineItems.forEach(item => {
        const id = item.order_line_item_id
        if (id) {
          // If ID exists, it will overwrite previous entry, effectively keeping the last one (largest index)
          uniqueMap.set(id, item)
        }
      })

      return Array.from(uniqueMap.values())
    },
    hasZeroStockItem() {
      return this.form.items.some(item => (item.availability === 0 || item.availability === '0') && item.sku)
    },
    zeroStockItems() {
      return this.form.items.filter(item => (item.availability === 0 || item.availability === '0') && item.sku)
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      this.isLoading = true
      await Promise.all([
        this.fetchWarehouseList(),
        this.fetchFulfillmentDetail(),
      ])
      .finally(() => this.isLoading = false)
    },
    async fetchFulfillmentDetail() {
      await getFulfillmentDetail({
        url: 'fulfillment',
        fulfillment_id: this.$route.params.id,
      })
      .then(async ({ data: { data: response } }) => {
        const { business_id, warehouse_id, order: { order_date, order_number, channel: { name: channelName } }, fulfillments } = response
        const { fulfillment_number, booking_state, delivery_order_state, line_items } = fulfillments?.[0]
        this.form = {
          orderDate: this.formatDate(order_date, true),
          orderNumber: order_number,
          fulfillmentNumber: fulfillment_number,
          business: this.$store.state.user.businessList.find(b => b.business_id === business_id)?.business_name,
          storeName: channelName,
          warehouse: warehouse_id,
          bookingStatus: booking_state
            ? this.bookingState[booking_state]?.title
            : '',
          status: setWordingFulfillmentState(delivery_order_state),
          items: line_items?.length
            ? await Promise.all(line_items.map(async item => {
                const catalogs = await this.fetchCatalogList(warehouse_id, item?.sku)
                return {
                  product_id: item?.id,
                  catalog_id: catalogs?.catalog_master_id || item?.catalog_id,
                  sku: item?.sku,
                  quantity: item?.quantity,
                  title: item?.catalog_title,
                  uom: item?.uom,
                  availability: catalogs?.availability,
                  is_on_warehouse: catalogs?.is_on_warehouse,
                  order_item_reference_id: item?.order_line_item_id,
                }
              }))
            : [],
        }
        this.originalLineItems = line_items || []
        this.detail = response
      })
      .catch(() => {
        this.form = {
          orderDate: '',
          orderNumber: '',
          fulfillmentNumber: '',
          business: '',
          storeName: '',
          warehouse: '',
          bookingStatus: '',
          status: '',
          items: [],
        }
        this.detail = null
      })
    },
    async fetchCatalogList(warehouse_id, sku) {
      if (!warehouse_id || !sku) return { availability: 0, is_on_warehouse: false }

      return await getCatalogListGR({
        business_id: this.businessId,
        warehouse_id,
        search: sku,
        page: 1,
        limit: 20,
      })
      .then(({ data: { data: response } }) => {
        const result = { catalog_master_id: null, availability: 0, is_on_warehouse: false }
        if (response?.length) {
          response?.forEach(res => {
            if (res?.sku === sku) {
              result.availability = res?.availability || 0
              result.is_on_warehouse = true
              result.catalog_master_id = res?.id
            }
          })
        }
        return result
      })
      .catch(() => ({ availability: 0, is_on_warehouse: false }))
    },
    async handleChangeWarehouse(value) {
      this.isLoading = true
      this.form.items = await Promise.all(this.form.items.map(async item => {
        const catalogs = await this.fetchCatalogList(value, item?.sku)
        return {
          ...item,
          availability: catalogs?.availability,
          is_on_warehouse: catalogs?.is_on_warehouse,
        }
      }))
      .finally(() => this.isLoading = false)
    },
    submitRevision() {
      if (!this.form.items.length) {
        this.$message.error(this.$t('fulfillment.detailItemProductEmpty'))
        return
      }

      this.$refs.ruleForm.validate(valid => {
        if (valid) {
          if (this.form.items.length) {
            let skus = []
            this.form.items.forEach(item => {
              if (!item.is_on_warehouse) skus.push(item.sku)
            })

            if (skus.length) {
              this.$message.error(this.$t('fulfillment.skuNotInSelectedWarehouse', { skus: skus.join(', ') }))
              return
            }
          }
          
          if (this.hasZeroStockItem) {
            const h = this.$createElement
            const productNames = this.zeroStockItems.map(item => item.title || item.sku).join(', ')
            
            this.$confirm({
              title: this.$t('fulfillment.confirmRevisionDoTitle'),
              content: h('div', [
                h('div', { style: { marginBottom: '12px', fontSize: '14px', color: '#595959' } }, [
                  this.$t('fulfillment.confirmRevisionDoDesc'),
                  h('strong', { style: { color: '#ff4d4f' } }, this.$t('fulfillment.zeroStockStatus')),
                  ':',
                ]),
                h('div', {
                  style: {
                    backgroundColor: '#fff1f0',
                    border: '1px solid #ffa39e',
                    padding: '10px 14px',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#cf1322',
                    maxHeight: '100px',
                    overflowY: 'auto',
                  },
                }, productNames),
                h('div', { style: { fontSize: '14px', fontWeight: '500', color: '#262626' } }, this.$t('fulfillment.confirmRevisionDoQuestion')),
              ]),
              icon: () => h('a-icon', { props: { type: 'exclamation-circle', theme: 'filled' }, style: { color: '#faad14', fontSize: '22px' } }),
              okText: this.$t('fulfillment.continueRevision'),
              cancelText: this.$t('common.back'),
              centered: true,
              width: 520,
              okButtonProps: { props: { type: 'primary', danger: true } },
              onOk: () => {
                this.isLoading = true
                this.saveRevision()
              },
            })
          } else {
            this.isLoading = true
            this.saveRevision()
          }
        }
      })
    },
    saveRevision: debounce(async function() {
      const { business_id, fulfillments, order: { id, order_date, order_number, state, channel: { id: channelId } } } = this.detail
      const {
        created_at,
        delivery_method,
        destination: { name: destinationName, phone: destinationPhone, address: { line1, line2, postal_code, country, province, city, district, sub_district } },
        sender: { name: senderName, phone: senderPhone, address: senderAddress },
        shipping: { courier_id, courier, service_type, service_name, shipping_cost, insurance_cost, shipping_label_url, awb },
      } = fulfillments?.[0]
      
      await submitFulfillmentRevision({
        fulfillment_id: this.$route.params.id,
        business_id,
        data: [{
          workspace_id: this.$route.query.workspace_id,
          business_id,
          warehouse_id: this.form.warehouse,
          channel_id: channelId,
          order_id: id,
          order_date: this.formatDate(order_date),
          order_number,
          order_state: state,
          fulfillment_items: this.form.items,
          delivery_method,
          customer_data: {
            first_name: destinationName,
            last_name: '',
            email: '',
            phone: destinationPhone,
          },
          sender_data: {
            name: senderName,
            phone: senderPhone,
            address: senderAddress,
          },
          destination_data: {
            first_name: destinationName,
            last_name: '',
            phone: destinationPhone,
            email: '',
            address: {
              line1,
              line2,
              postal_code,
              country,
              province,
              city,
              district,
              sub_district,
            },
          },
          shipping_data: {
            courier_id,
            courier,
            service_type,
            service_name,
            shipping_cost,
            insurance_cost,
            shipping_label_url,
            awb,
          },
          goods_value: {
            value: 0,
            currency: 'IDR',
          },
          sla: this.formatDate(this.$route.query.sla),
          created_at: this.formatDate(created_at),
        }],
      })
      .then(() => {
        this.$notification.success({ message: this.$t('fulfillment.revisionSavedSuccess') })
        this.goBack()
      })
      .catch(() => this.$notification.error({ message: this.$t('fulfillment.revisionSavedFailed') }))
      .finally(() => this.isLoading = false)
    }, 600),
    fetchWarehouseList() {
      if (this.$store.state.user.restriction_base?.toLowerCase() !== 'warehouse') {
        let defaultBusinessId = this.businessId
        if (Array.isArray(defaultBusinessId) && defaultBusinessId.length > 0) {
          defaultBusinessId = defaultBusinessId.join(',');
        }
        this.$store.dispatch('warehouse/GETSALESWAREHOUSE', { business_id: defaultBusinessId })
        .then(({ data }) => {
          this.warehouseList = data?.map(w => ({
            value: w.id,
            label: w.name,
          }))
        })
        .catch(() => this.warehouseList = [])
      }
    },
    handleSearch: debounce(async function(value) {
      this.fetching = true
      const bodyValue = {
        business_id: this.businessId,
        warehouse_id: this.form.warehouse,
        search: value,
        page: 1,
        limit: 20,
      }
      await getCatalogListGR(bodyValue)
        .then(({ data: { data: response } }) => this.inventories = response)
        .catch(err => {
          this.inventories = []
          console.error(err)
        })
        .finally(() => this.fetching = false)
    }, 500),
    handleChange(value, index) {
      const item = value.split('.')
      this.form.items[index].product_id = item[0]
      this.form.items[index].catalog_id = item[1]
      this.form.items[index].sku = item[2]
      this.form.items[index].title = item[3]
      this.form.items[index].uom = item[4]
      this.form.items[index].availability = item[5]
      this.form.items[index].is_on_warehouse = true
    },
    handleResetSearch() {
      this.inventories = []
    },
    getReferencedItem(referenceId) {
      if (!referenceId) return null
      return this.distinctOriginalLineItems.find(li => li.order_line_item_id === referenceId) || null
    },
    validateQty(rule, value, callback) {
      if (value == 0) {
        callback(new Error(this.$t('fulfillment.qtyCannotBeZero')))
      } else if (value == '') {
        callback(new Error(this.$t('fulfillment.qtyRequired')))
      } else {
        callback()
      }
    },
    handleAddItem() {
      this.selectedReferenceId = null
      this.addItemModalVisible = true
    },
    handleConfirmAddItem() {
      this.addItemModalVisible = false
      this.form.items.push({
        product_id: '',
        catalog_id: '',
        sku: undefined,
        quantity: 1,
        title: '',
        uom: '',
        availability: 0,
        is_on_warehouse: false,
        order_item_reference_id: this.selectedReferenceId,
      })
      this.selectedReferenceId = null
    },
    handleDeleteItem(index) {
      const item = this.form.items[index]
      if (item.sku && this.itemsWithSku.length <= 1) {
        this.$message.warning(this.$t('fulfillment.cannotDeleteLastItem'))
        return
      }
      this.form.items.splice(index, 1)
    },
    formatDate(date, useFormat = false) {
      if (date) {
        const value = Number(date) < 1e12 ? Number(date) * 1000 : Number(date)
        return this.$moment(value).format(useFormat ? 'DD MMM YYYY - HH:mm' : '')
      }
      return null
    },
    ellipsisText(text) {
      if(!text) return '-'
      return text.length > 29 ? `${text.substring(0, 26)}...` : text
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0]?.text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    },
    goBack() {
      this.$router.push({
        path: '/fulfillment',
        query: {
          ...this.$route.query,
          business_ids: undefined,
          order_date: undefined,
          sla: undefined,
        },
      })
    },
  },
}
</script>

<style lang="scss">
.fulfillment-revision {
  .page-title {
    font-size: 1.5rem;
    font-weight: bold;
  }

  .ant-card {
    &-head {
      min-height: 0;
      
      &-title {
        padding: 12px 0;
      }
    }

    &-extra {
      padding: 8px 0;
    }

    &-body {
      padding: 12px 24px;
    }
  }

  .ant-form-item {
    margin-bottom: 12px;
  }

  .ant-form-item-label, label {
    line-height: normal;
  }

  .ant-input[disabled] {
    background-color: #f1f4f7;
  }

  table {
    th {
      background-color: #f0f2f4;
    }

    td {
      border-bottom: 1px solid #e4e9f0;
    }

    th, td {
      padding: 10px;
    }
  }

  .footer {
    width: 100%;
    display: block;
    position: sticky;
    padding: 15px 40px;
    right: 0;
    bottom: 0;
    background-color: #fff;
    text-align: right;
    box-shadow: 0px 3px 7px 1px rgba(0, 0, 0, 0.05);
    z-index: 9;
  }
}

.ref-badge {
  display: inline-flex;
  align-items: center;
  background-color: #f0f7ff;
  border: 1px solid #91caff;
  border-radius: 4px;
  padding: 2px 7px;
  cursor: default;
  max-width: 100%;

  &__sku {
    font-size: 11px;
    font-weight: 600;
    color: #1677ff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 120px;
  }
}

.reference-item-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e4e9f0;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    border-color: #1890ff;
    background-color: #f0f7ff;
  }

  &--selected {
    border-color: #1890ff;
    background-color: #e6f4ff;
  }

  &__radio {
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-weight: 500;
    font-size: 13px;
    color: #222;
  }

  &__sku {
    font-size: 12px;
    color: #888;
    margin-top: 2px;
  }
}

.ant-select-dropdown-menu-item-group {
  margin-bottom: 4px !important;
}

.ant-select-dropdown-menu-item-group-title {
  height: auto !important;
  line-height: normal !important;
  
  span {
    font-weight: bold;
  }
}

.ant-select-dropdown-menu-item-group-list > .ant-select-dropdown-menu-item {
  padding: 0px 12px 8px !important;
}

.no-border-row td {
  border-bottom: none !important;
}
</style>